import { computed, defineAsyncComponent, markRaw } from 'vue';
import { storeToRefs } from 'pinia';
import { injectLocal, provideLocal } from '@vueuse/core';
import { sanitize } from '@vue-util/sanitize';
import useUpc from '~/modules/upc/composables/useUpc';
import { useBrand } from '~/composables/useBrand';
import { useProductType } from '~/composables/useProductType';
import { useSlideout } from '~/composables/utils/useSlideout';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { PRODUCT_DETAILS_REPOSITORY } from '../constants/providers';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import { getMemberProduct } from '~/utils/product';

import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';
import useExperiments from '~/stores/useExperimentsStore';

export const useProductDetails = () => injectLocal(PRODUCT_DETAILS_REPOSITORY);

export const useProductDetailsProvide = () => {
  const { productData } = useProduct();
  const { isConsolidatedPage, isConsolidatedStandard, isConsolidatedMaster, isConsolidatedLatchKey } = useProductType();
  const { showSlideout } = useSlideout();
  const { upcData, selectedUpcId } = useUpc();
  const { processedProdDescFlag } = useProductFlags();
  const { memberFlags } = storeToRefs(useConsolidatedMemberStore());
  const { isBcom } = useBrand();
  const { isShopTheSetExpEnabled } = useFeatureEligibility();
  const selectedMember = useSelectedMemberId();
  const {
    experimentFlags: { isProductHighlightsEnabled },
  } = useExperiments();

  const MEMBER_BULLETS_THRESHOLD = 5;

  function _getFallbackMasterDetail(memberDetail, masterDetail) {
    const isProcessedProdDescEnabled = isShopTheSetExpEnabled ? memberFlags.value?.processedProdDescFlag : processedProdDescFlag;
    const memberBullets = isProcessedProdDescEnabled ? memberDetail.processedProdDesc?.productDetails || [] : memberDetail.bulletText || [];

    if (memberBullets.length <= MEMBER_BULLETS_THRESHOLD) {
      return {
        ...masterDetail,
        highlights: memberDetail.highlights,
      };
    }
    return null;
  }

  /** @returns {Product['detail'] | undefined} */
  function _getProductDetails() {
    const masterDetail = productData.detail;

    if (isProductHighlightsEnabled) {
      const isConsolidatedOrShopTheSet = isShopTheSetExpEnabled || isConsolidatedPage;
      if (isConsolidatedOrShopTheSet && selectedMember.value) {
        const memberDetail = getMemberProduct(productData, selectedMember.value)?.detail;

        if (memberDetail && (isConsolidatedMaster || isConsolidatedLatchKey)) {
          const fallbackDetail = _getFallbackMasterDetail(memberDetail, masterDetail);
          if (fallbackDetail) return fallbackDetail;
        }

        return memberDetail || masterDetail;
      }
      return masterDetail;
    }

    if (isShopTheSetExpEnabled && selectedMember.value) {
      return getMemberProduct(productData, selectedMember.value)?.detail;
    }

    if (isConsolidatedStandard && selectedUpcId.value) {
      return productData.relationships?.upcs?.[selectedUpcId.value]?.detail;
    }

    return masterDetail;
  }

  /** @returns {import('vue').ComputedRef<Product['detail']>} */
  const productDetails = computed(_getProductDetails);

  /** @returns {string} */
  function _getProductDescription() {
    const description = productDetails.value?.description?.trim();

    if (description?.endsWith('<br><br>FEATURES')) {
      return sanitize(description?.split('<br><br>FEATURES')[0]);
    }
    return sanitize(description || '');
  }

  /**
   * @typedef {Object} BulletItem
   * @property {string} type
   * @property {string} text
   * @returns {BulletItem[]}
   * */
  function _getProductSummaryBullets() {
    const isProcessedProdDescEnabled = isShopTheSetExpEnabled ? memberFlags.value?.processedProdDescFlag : processedProdDescFlag;
    const textBullets = isProcessedProdDescEnabled
      ? productDetails.value?.processedProdDesc?.productDetails || []
      : productDetails.value?.bulletText || [];

    const mappedTextBullets = textBullets.map((bullet) => ({
      type: 'TEXT',
      text: sanitize(bullet),
    }));
    const bulletLinks = Array.isArray(productDetails.value?.bulletLinks) ? productDetails.value.bulletLinks : [];
    return [...mappedTextBullets, ...bulletLinks];
  }

  /** @returns {string} */
  function _getProductDimensionsTitle() {
    return sanitize(productDetails?.value?.dimensionsHeaderText || '');
  }

  /** @returns {string[]} */
  function _getProductDimensionsBullets() {
    const bullets = productDetails?.value?.dimensionsBulletText || [];
    return bullets.map(sanitize);
  }

  /** @returns {BulletItem[]} */
  function _getMaterialsAndCareBullets() {
    const { processedProdDesc, materialsAndCare } = productDetails?.value || {};
    const bullets = materialsAndCare || processedProdDesc?.fabricAndCare || [];

    const mappedTextBullets = bullets.map((bullet) => ({
      type: 'TEXT',
      text: sanitize(bullet),
    }));

    const hasHiddenFillDisclosure = false; // backend flag, change it
    if (hasHiddenFillDisclosure) {
      const additionalBullet = {
        type: 'LABEL_DISCLOSURE',
        text: 'Product labels for all quilted items (filled apparel, home textiles, and accessories) are available online.',
      };
      return [...mappedTextBullets, additionalBullet];
    }

    return mappedTextBullets;
  }

  /** @returns {string[]} */
  function _getSizeAndFitBullets() {
    const dimensionsBulletsLocal = _getProductDimensionsBullets();
    const { processedProdDesc } = productDetails?.value || {};

    return isBcom ? dimensionsBulletsLocal || [] : (processedProdDesc?.sizeAndFit || []).map(sanitize);
  }

  function openProductFillLegalDisclosureDrawer() {
    showSlideout({
      id: 'product-fill-legal-disclosure',
      title: 'Product Fill Legal Disclosure',
      props: { availableUpcs: Object.keys(upcData.value.upcs ?? {}) },
      component: markRaw(
        defineAsyncComponent(
          /* istanbul ignore next */ () => import(/* webpackChunkName: "[request]" */ '../components/slideouts/ProductFillLegalDisclosureDrawer.vue'),
        ),
      ),
    });
  }

  provideLocal(PRODUCT_DETAILS_REPOSITORY, {
    openProductFillLegalDisclosureDrawer,
    availableUpcs: Object.keys(upcData.value.upcs ?? {}),
    productDescription: computed(_getProductDescription),
    productSummaryBullets: computed(_getProductSummaryBullets),
    dimensionsTitle: computed(_getProductDimensionsTitle),
    dimensionsBullets: computed(_getProductDimensionsBullets),
    materialsAndCareBullets: computed(_getMaterialsAndCareBullets),
    sizeAndFit: computed(_getSizeAndFitBullets),
    highlights: computed(() => productDetails.value?.highlights || []),
    typeName: computed(() => productDetails.value?.typeName || ''),
  });
};
